export function normalizeBool(value) {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  if (typeof value === 'string') {
    const lowered = value.trim().toLowerCase();
    return lowered === '1' || lowered === 'true' || lowered === 'yes';
  }
  return Boolean(value);
}

export function parseUtcDate(value) {
  if (value === null || value === undefined) {
    return null;
  }
  let normalized = String(value).trim();
  if (!normalized) {
    return null;
  }
  if (normalized.includes(' ') && !normalized.includes('T')) {
    normalized = normalized.replace(' ', 'T');
  }
  if (!/[zZ]$/.test(normalized) && !/[+-]\d{2}:\d{2}$/.test(normalized)) {
    normalized = `${normalized}+00:00`;
  }
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed;
}

export function normalizeUtcInput(value, allowEmpty = true) {
  if (value === null || value === undefined) {
    return allowEmpty ? null : '';
  }
  let normalized = String(value).trim();
  if (!normalized) {
    return allowEmpty ? null : '';
  }
  if (normalized.includes(' ') && !normalized.includes('T')) {
    normalized = normalized.replace(' ', 'T');
  }
  if (!/[zZ]$/.test(normalized) && !/[+-]\d{2}:\d{2}$/.test(normalized)) {
    normalized = `${normalized}+00:00`;
  }
  return normalized;
}

export function formatUtcDisplay(value) {
  const date = parseUtcDate(value);
  if (!date) {
    return value || '—';
  }
  return date.toISOString().replace('.000Z', 'Z');
}

export function formatLocalDisplay(value) {
  const date = parseUtcDate(value);
  if (!date) {
    return '—';
  }
  const year = String(date.getFullYear());
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

export function buildPreviewUrl(origin, pathname, token) {
  if (!token) {
    return '';
  }
  const parts = String(pathname || '').split('/').filter(Boolean);
  const lang = parts.length > 0 ? parts[0] : 'en';
  return `${origin}/${lang}/admin/previewtasks.html?token=${encodeURIComponent(token)}`;
}

export function buildWrittenUploadOverrideUrl(origin, pathname, token) {
  if (!token) {
    return '';
  }
  const parts = String(pathname || '').split('/').filter(Boolean);
  const lang = parts.length > 0 ? parts[0] : 'en';
  return `${origin}/${lang}/admin/writtenuploadoverride.html?token=${encodeURIComponent(token)}`;
}

export function applyNeedProctoringPrecedence(flags) {
  const next = {
    need_proctoring: normalizeBool(flags?.need_proctoring) ? 1 : 0,
    need_mic: normalizeBool(flags?.need_mic) ? 1 : 0,
    need_screen: normalizeBool(flags?.need_screen) ? 1 : 0,
    need_camera: normalizeBool(flags?.need_camera) ? 1 : 0,
    need_answer_input: normalizeBool(flags?.need_answer_input) ? 1 : 0,
  };
  if (next.need_proctoring) {
    next.need_mic = 0;
    next.need_screen = 0;
    next.need_camera = 0;
    next.need_answer_input = 0;
  }
  return next;
}

export function computeNeedFieldUiState(flags) {
  const applied = applyNeedProctoringPrecedence(flags);
  const disabled = Boolean(applied.need_proctoring);
  return {
    need_proctoring: {checked: Boolean(applied.need_proctoring), disabled: false},
    need_mic: {checked: Boolean(applied.need_mic), disabled},
    need_screen: {checked: Boolean(applied.need_screen), disabled},
    need_camera: {checked: Boolean(applied.need_camera), disabled},
    need_answer_input: {checked: Boolean(applied.need_answer_input), disabled},
  };
}

export function buildUpdatePayloadFromInputs(fields, needFlags) {
  const appliedNeed = applyNeedProctoringPrecedence(needFlags);
  const rawWrittenUploadDuration = String(fields.written_upload_duration ?? '').trim();
  return {
    id: Number(fields.id),
    exam_template_id: Number(fields.exam_template_id),
    school: fields.school,
    duration: Number(fields.duration),
    written_upload_duration: rawWrittenUploadDuration === '' ? null : Number(rawWrittenUploadDuration),
    state: fields.state,
    iframe_url: String(fields.iframe_url || '').trim(),
    start_available: normalizeUtcInput(fields.start_available, false),
    end_available: normalizeUtcInput(fields.end_available, false),
    first_opened_at: normalizeUtcInput(fields.first_opened_at, true),
    started_at: normalizeUtcInput(fields.started_at, true),
    finished_at: normalizeUtcInput(fields.finished_at, true),
    finished_upload_at: normalizeUtcInput(fields.finished_upload_at, true),
    need_proctoring: appliedNeed.need_proctoring,
    need_mic: appliedNeed.need_mic,
    need_screen: appliedNeed.need_screen,
    need_camera: appliedNeed.need_camera,
    need_answer_input: appliedNeed.need_answer_input,
  };
}

export function validateTaskLinkPayload(payload, {allowedStateValues = []} = {}) {
  if (!payload.id || Number.isNaN(payload.id)) {
    return {ok: false, field: 'id', error: 'Link ID is required.'};
  }
  if (!payload.exam_template_id || Number.isNaN(payload.exam_template_id)) {
    return {ok: false, field: 'exam_template_id', error: 'Exam template is required.'};
  }
  if (!payload.school) {
    return {ok: false, field: 'school', error: 'School is required.'};
  }
  if (!payload.state) {
    return {ok: false, field: 'state', error: 'State is required.'};
  }
  if (Array.isArray(allowedStateValues) && allowedStateValues.length && !allowedStateValues.includes(payload.state)) {
    return {ok: false, field: 'state', error: 'State is not allowed.'};
  }
  if (!payload.duration || Number.isNaN(payload.duration) || payload.duration <= 0) {
    return {ok: false, field: 'duration', error: 'Duration must be a positive number.'};
  }
  if (payload.written_upload_duration !== null && payload.written_upload_duration !== undefined) {
    if (Number.isNaN(payload.written_upload_duration) || payload.written_upload_duration <= 0) {
      return {ok: false, field: 'written_upload_duration', error: 'Written upload duration must be a positive number or empty.'};
    }
  }

  const start = parseUtcDate(payload.start_available);
  const end = parseUtcDate(payload.end_available);
  if (!start) {
    return {ok: false, field: 'start_available', error: 'Start available must be a valid UTC datetime.'};
  }
  if (!end) {
    return {ok: false, field: 'end_available', error: 'End available must be a valid UTC datetime.'};
  }
  if (end < start) {
    return {ok: false, field: 'end_available', error: 'End available cannot be earlier than start available.'};
  }

  const optionalDateFields = ['first_opened_at', 'started_at', 'finished_at', 'finished_upload_at'];
  for (const field of optionalDateFields) {
    if (payload[field] && !parseUtcDate(payload[field])) {
      return {ok: false, field, error: `${field} must be empty or a valid UTC datetime.`};
    }
  }

  const firstOpenedAt = parseUtcDate(payload.first_opened_at);
  const startedAt = parseUtcDate(payload.started_at);
  const finishedAt = parseUtcDate(payload.finished_at);
  const finishedUploadAt = parseUtcDate(payload.finished_upload_at);

  if (finishedUploadAt && !finishedAt) {
    return {ok: false, field: 'finished_upload_at', error: 'Finished upload requires Finished At.'};
  }
  if (finishedUploadAt && payload.written_upload_duration == null) {
    return {ok: false, field: 'finished_upload_at', error: 'Finished upload is allowed only when written upload duration is set.'};
  }
  if (startedAt && finishedAt && finishedAt < startedAt) {
    return {ok: false, field: 'finished_at', error: 'Finished At cannot be earlier than Started At.'};
  }
  if (finishedAt && finishedUploadAt && finishedUploadAt < finishedAt) {
    return {ok: false, field: 'finished_upload_at', error: 'Finished upload cannot be earlier than Finished At.'};
  }
  if (startedAt && finishedAt) {
    const solveDeadline = new Date(startedAt.getTime() + payload.duration * 60 * 1000);
    if (finishedAt > solveDeadline) {
      return {ok: false, field: 'finished_at', error: 'Finished At cannot exceed Started At + Duration.'};
    }
  }
  if (finishedAt && finishedUploadAt && payload.written_upload_duration != null) {
    const uploadDeadline = new Date(finishedAt.getTime() + payload.written_upload_duration * 60 * 1000);
    if (finishedUploadAt > uploadDeadline) {
      return {ok: false, field: 'finished_upload_at', error: 'Finished upload cannot exceed Finished At + Written upload duration.'};
    }
  }

  if (payload.state === 'not_op') {
    if (firstOpenedAt || startedAt || finishedAt || finishedUploadAt) {
      return {ok: false, field: 'state', error: 'State not_op requires all activity timestamps to be empty.'};
    }
  }
  if (payload.state === 'op_not_start') {
    if (startedAt || finishedAt || finishedUploadAt) {
      return {ok: false, field: 'state', error: 'State op_not_start requires Started/Finished timestamps to be empty.'};
    }
  }
  if (payload.state === 'start_not_fin') {
    if (!startedAt || finishedAt || finishedUploadAt) {
      return {ok: false, field: 'state', error: 'State start_not_fin requires Started At and forbids finish timestamps.'};
    }
  }
  if (payload.state === 'fin_written_upload') {
    if (!startedAt || !finishedAt || finishedUploadAt || payload.written_upload_duration == null) {
      return {ok: false, field: 'state', error: 'State fin_written_upload requires Started At, Finished At, written upload duration, and empty Finished Upload At.'};
    }
  }
  if (payload.state === 'fin') {
    if (!startedAt || !finishedAt) {
      return {ok: false, field: 'state', error: 'State fin requires Started At and Finished At.'};
    }
    if (payload.written_upload_duration == null && finishedUploadAt) {
      return {ok: false, field: 'finished_upload_at', error: 'Finished upload must stay empty when there is no written upload window.'};
    }
    if (payload.written_upload_duration != null && !finishedUploadAt) {
      return {ok: false, field: 'finished_upload_at', error: 'State fin with written upload requires Finished Upload At.'};
    }
  }
  return {ok: true};
}

export function confirmDeleteAction({rowId, studentEmail, confirmFn = window.confirm, promptFn = window.prompt}) {
  const firstConfirm = confirmFn(
    `Delete link #${rowId} (${studentEmail || 'no email'})?\n\nThis action is irreversible.`
  );
  if (!firstConfirm) {
    return {ok: false, reason: 'first_confirm_cancelled'};
  }
  const secondConfirm = promptFn('Type DELETE to confirm removal:');
  if (secondConfirm !== 'DELETE') {
    return {ok: false, reason: 'second_confirm_failed'};
  }
  return {ok: true};
}
